import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { LoadingButton } from "@mui/lab";
import { Grid, Stack } from "@mui/material";
import { FileSection, FormTextField } from "components/atoms";
import { DetailTableTabs, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { DialogHelper, RoleHelper, StatusHelper } from "helpers";
import { useRequest } from "hooks/useRequest";
import {
  ELoadingOrderStatus,
  IDownloadExcelRequest,
  IDownloadLODeliveryProgressRequest,
  IGetByIdRequest,
  ILoadingOrder,
  IUpdateLoadingOrderDocumentRequest,
} from "model";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { DownloadActions, LoadingOrderActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import LoadingOrderApiService from "services/LoadingOrderApiService";
import { EXPORT_FILE_NAME } from "utils/Const";
import i18n, { I18nTranslator, _t, translations } from "utils/i18n";
import {
  DeliveryProgress,
  PalletList,
  ProductList,
  RelatedDocumentList,
} from "./components";
const _fileId = "import-receive-document";

const LoadingOrderDetail = () => {
  const params = useParams<{
    id: string;
    doImportCode: string;
    description: string;
  }>();
  const fileRef = React.useRef<any>();

  const action = useAppSelector((state) => state.ReceiveReducer.action);
  const dispatch = useAppDispatch();
  const [request] = useState({ id: params.id });
  const { loading, data } = useRequest<IGetByIdRequest, ILoadingOrder>(
    request,
    LoadingOrderApiService.getLOById
  );

  const handleFileSelected = (files: FileList) => {
    if (files) {
      const file = files.item(0);
      DialogHelper.openConfirmImportDialog({
        title: "Nhập phiếu nhập hàng",
        fileName: file?.name!,
        onConfirm: () =>
          dispatch(
            LoadingOrderActions.importLoadingOrderDocument.request({
              loadingOrderHeaderId: data?.id!,
              LOImportCode: data?.doImportCode!,
              loadingOrderHeaderDescription: data?.description || "",
              file: file as File,
            })
          ),
      });
    }
  };

  const tabsName = [
    I18nTranslator(translations.receive.productList),
    I18nTranslator(translations.receive.deliveryProgress),
    I18nTranslator(translations.receive.palletList),
    I18nTranslator(translations.receive.relatedDocumentList),
  ];

  const tabsPanel = [
    <ProductList deliveryOrderHeaderId={params.id} />,
    <DeliveryProgress deliveryOrderHeaderId={params.id} />,
    <PalletList doImportCode={params.doImportCode} />,
    <RelatedDocumentList doImportCode={params.doImportCode} />,
  ];

  return (
    <>
      <FileSection
        ref={fileRef}
        id={_fileId}
        onFileSelected={handleFileSelected}
      />
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          height: "100%",
        }}
      >
        <DetailHeader loading={loading} data={data} />
        <DetailTableTabs
          tabsName={tabsName}
          tabsPanel={tabsPanel}
          hideHeaderButton
        />
      </div>
    </>
  );
};

const DetailHeader = (props: { data?: ILoadingOrder; loading: boolean }) => {
  const { loading, data } = props;
  const params = useParams<{
    id: string;
    doImportCode: string;
  }>();
  const dispatch = useAppDispatch();
  const action = useAppSelector((state) => state.ReceiveReducer.action);
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );

  const formik = useFormik<ILoadingOrder>({
    initialValues: data || ({} as ILoadingOrder),
    enableReinitialize: true,
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      validateForm().then(() => {
        const request: IUpdateLoadingOrderDocumentRequest = {
          id: values.id!,
          doImportCode: values.doImportCode!,
          deliveryDate: values.deliveryDate!,
          description: values.description || "",
          isExportFull: values.isExportFull!,
          isSplitFull: values.isSplitFull!,
          isPrepareFull: values.isPrepareFull!,
          isDeliveryFull: values.isDeliveryFull!,
          status: values.status!,
        };
        dispatch(LoadingOrderActions.updateLODocument.request(request));
      });
    },
  });

  const onDownloadLo = async () => {
    const request: IDownloadExcelRequest = {
      id: params.id,
      fileName: `${EXPORT_FILE_NAME.loadingOrder}_[${params.doImportCode}]`,
    };
    dispatch(DownloadActions.downloadLODocumentById.request(request));
  };
  const onDownloadLoDeliveryProgress = async () => {
    const request: IDownloadLODeliveryProgressRequest = {
      loadingOrderHeaderId: params.id,
      fileName: `${EXPORT_FILE_NAME.loadingOrderDeliveryProgress}_[${params.doImportCode}]`,
    };
    dispatch(DownloadActions.downloadLODeliveryProgress.request(request));
  };

  return (
    <ReportHeader
      title={I18nTranslator(translations.receive.loDetail)}
      description={params.doImportCode}
      formik={formik}
      loading={
        loading ||
        action.includes(LoadingOrderActions.updateLODocument.requestName)
      }
      confirmButtonText={I18nTranslator(translations.button.save)}
      confirmButtonIcon={<SaveIcon />}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <label htmlFor={_fileId}>
            <LoadingButton
              component={"span"}
              startIcon={<UploadFileIcon />}
              variant="contained"
              disabled={
                !(
                  RoleHelper.isSuperAdmin() ||
                  RoleHelper.isAdmin() ||
                  RoleHelper.isPlanner()
                )
              }
            >
              {i18n.t(_t(translations.receive.importDocs))}
            </LoadingButton>
          </label>
          <LoadingButton
            loading={downloading}
            onClick={onDownloadLo}
            startIcon={<DownloadIcon />}
            variant="contained"
            disabled={
              !(
                RoleHelper.isSuperAdmin() ||
                RoleHelper.isAdmin() ||
                RoleHelper.isPlanner()
              )
            }
          >
            {i18n.t(_t(translations.receive.downloadLO))}
          </LoadingButton>
          <LoadingButton
            loading={downloading}
            onClick={onDownloadLoDeliveryProgress}
            startIcon={<DownloadIcon />}
            variant="contained"
            disabled={
              !(
                RoleHelper.isSuperAdmin() ||
                RoleHelper.isAdmin() ||
                RoleHelper.isPlanner()
              )
            }
          >
            {i18n.t(_t(translations.receive.downloadDeliveryProcess))}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.description)}
            placeholder={I18nTranslator(translations.placeholder.des)}
            id="description"
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.status)}
            id="status"
            disabled
            renderValue={() =>
              StatusHelper.getLOStatusString(
                formik.values.status! as ELoadingOrderStatus
              )
            }
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

export default LoadingOrderDetail;
